import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import * as postRobot from 'post-robot';
import { TokenHolder } from './_core/utils/token-holder.model';

import * as Sentry from '@sentry/angular';

import { Userpilot } from 'userpilot';
import packageJson from '../../package.json';

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
    /**
     * Iframe Element and Window
     */
    private umsFrameElement = null;
    private childWindow: Window = null;

    /**
     * Timer that checks presence of the iframes
     */
    private timer: any = null;

    /**
     * Boolean to know if its first load
     * to allow the app to start
     */
    private isFirstLoad = true;

    /**
     * TokenHolder in use
     */
    private tokenHolder: TokenHolder = null;

    private trackerInit = false;

    constructor() {}

    /**
     * Initializer
     * Runs before the app
     */
    public initializeApp(): Promise<void> {
        return new Promise<void>((resolve) => {
            let env = 'unknown';
            if (location.origin.includes('localhost')) {
                env = 'local';
            } else if (location.origin.includes('dev')) {
                env = 'develop';
            } else if (location.origin.includes('staging')) {
                env = 'staging';
            } else if (environment.production) {
                env = 'production';
            } else {
                setTimeout(() => { console.error(`Unknown environment: ${location.origin}`); }, 1000);
            }

            Sentry.init({
                dsn: environment.sentryDNS,
                release: `CUP@${packageJson.version}`,
                environment: env,
                integrations: [
                    Sentry.captureConsoleIntegration({
                        levels: ['error', 'warn'],
                    }),
                    Sentry.httpClientIntegration()
                ],
            });

            this.crossOriginLocalStorage(resolve);
            this.startAuth();
        });
    }

    /**
     *
     * Waits until ums iframe is present
     * Clears the timer
     * Starts listening to iframe messages
     * Request for credentials
     *
     * @param resolve Promise resolve
     */
    private async startAuth() {
        if (await this.isFramePresent()) {
            clearInterval(this.timer);
            this.getAuthData();
        }
    }

    /**
     *
     * Requests the iframe for auth credentials
     *
     */
    private getAuthData() {
        const messageData = {
            method: 'get',
        };

        this.postMessage(messageData);
    }

    /**
     *
     * Listens to iframe messages
     *
     * @param resolve Promise resolve
     */
    private crossOriginLocalStorage(resolve) {
        postRobot.on('access#get', (event) => {
            this.parseAndSaveResponse(event.data, resolve, this);
        });
    }

    /**
     *
     * Request sender
     *
     * @param messageData Request
     */
    private postMessage(messageData) {
        postRobot.send(this.childWindow, 'get', messageData);
    }

    /**
     *
     * Parses response to JSON
     * Allows only known responses
     *
     * @param event Event
     * @param resolve Promise resolve
     * @param context this
     */
    private parseAndSaveResponse(event: any, resolve, context: this) {
        if( !!event && event.method === 'access#get' ) {
            context.saveAuthData(event.data, resolve);
        }
    }

    /**
     *
     * Preserves/edits auth data in runtime
     *
     * @param data TokenHolder
     * @param resolve Promise resolve
     */
    private saveAuthData(data, resolve) {
        this.tokenHolder = data;
        if (!this.trackerInit) {
            this.initTrackerUserPilot();
            this.trackerInit = true;
        }
        /* console.log(this.tokenHolder); */

        /* if (this.tokenHolder && this.tokenHolder.access_token) {
            const payload = this.secretService.getTokenPayload(this.tokenHolder.access_token);
            const portalList = !!payload ? JSON.parse(payload['https://supplystack.com/portalConfigs']) : [];
            const userBasic = payload['https://supplystack.com/user_basic'];
            const portal = portalList.find(p =>
                !!this.secretService.getConfiguration()
                && p.type === 'CP'
                && p.name === this.secretService.getConfiguration().configId
            );

            console.log(userBasic, portalList);
            if (!!portal) {
                this.secretService.setToken(this.tokenHolder.access_token);
                this.secretService.setUser(userBasic);
            } else {
                this.secretService.logout();
            }

        } else {
            this.secretService.logout();
        } */

        if (this.isFirstLoad) {
            this.isFirstLoad = false;
            resolve();
        }
    }

    public initTrackerUserPilot() {
        Userpilot.initialize('NX-e315df21');
    }

    /**
     *
     * Checks if iframe is already present on the view
     *
     */
    private isFramePresent(): Promise<boolean> {
        return new Promise((response) => {
            let hasSetUrl = false;

            this.umsFrameElement = document.getElementById('ums-frame');
            if (this.umsFrameElement) {
                if (!hasSetUrl) {
                    hasSetUrl = true;
                    this.umsFrameElement.src = environment.umsFrameUrl;
                }
            }

            try {
                this.childWindow = this.umsFrameElement.contentWindow;
            } catch {
                this.childWindow = this.umsFrameElement.contentWindow;
            }

            this.timer = setInterval(() => {
                postRobot
                    .send(this.childWindow, 'frameReady', 'try')
                    .then(() => {
                        response(true);
                    })
                    .catch(() => console.log('Frame not yet loaded'));
            }, 500);
        });
    }

    /**
     * Public method that returns a TokenHolder
     */
    public getToken(): TokenHolder {
        return this.tokenHolder;
    }

    public getTokenPayload(token) {
        let decodedToken = null;
        if (token) {
            const idTokenBody = token.split('.')[1];
            try {
                decodedToken = decodeURIComponent(escape(atob(idTokenBody)));
            } catch {
                decodedToken = window.atob(idTokenBody.replace(/-/g, '+').replace(/_/g, '/'));
            }
        }
        return decodedToken ? JSON.parse(decodedToken) : null;
    }

    public logout(theme: string) {
        location.href = `${environment.umsLoginUrl}/login?callback=${encodeURIComponent(
            location.href
        )}&signout=true&app=CP&theme=${theme}`;
    }
}
